/**
 * Definitive "no app access" 401 (gh #1263 follow-up).
 *
 * shared/auth.py answers 401 with `detail: { code: 'NO_APP_ACCESS', message }`
 * when the bearer verified but its holder has no app user, a deactivated
 * account, or no active tenant role. Every other 401 — an expired, revoked or
 * racing token, or a database error during the lookup — keeps a plain string
 * detail. The code is the only thing that tells the two apart.
 *
 * It has to end the session. A refreshed token gets the same answer, so
 * handling it as the transient `profile-401` reset kept Firebase signed in and
 * the /login existing-session sync sent the user straight back to the
 * dashboard, where it 401'd again.
 *
 * Framework-agnostic: no React, no browser globals.
 */

export const NO_APP_ACCESS_CODE = 'NO_APP_ACCESS';

export class NoAppAccessError extends Error {
  constructor() {
    // Deliberately NOT 'Failed to fetch user profile: 401': callers substring
    // match that message to fire the transient profile-401 reset.
    super('This account does not have access to the application');
    this.name = 'NoAppAccessError';
  }
}

export function isNoAppAccessError(error: unknown): error is NoAppAccessError {
  return error instanceof NoAppAccessError;
}

/** Reads `detail.code` from a FastAPI error body; null when absent or unparseable. */
export function parseErrorCode(body: string): string | null {
  try {
    const parsed: unknown = JSON.parse(body);
    const detail = (parsed as { detail?: unknown } | null)?.detail;
    if (detail && typeof detail === 'object') {
      const code = (detail as { code?: unknown }).code;
      if (typeof code === 'string') {
        return code;
      }
    }
  } catch {
    // Not JSON — e.g. an HTML error page from the load balancer.
  }
  return null;
}
